import {
  DefaultMetricOptions,
  Goal,
  GoalCompletion,
  GoalProgressCount,
  GoalTreeIndex,
  GoalType,
  ID,
} from '@griply/types';

import { getLatestMetricValue } from './goals/get-latest-metric-entry';
import { isTaskMeasuredGoal } from './goals/is-task-measured-goal';

export type GetGoalCompletionOptions = {
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  index?: GoalTreeIndex;
};

const getProgressCountCompletion = (
  goalId: ID,
  goalProgressCounts?: Map<ID, GoalProgressCount>,
): GoalCompletion => {
  // no live counts yet — report an unmeasurable target rather than a false 0%
  if (!goalProgressCounts) {
    return { start: 0, current: 0, end: Infinity };
  }

  const { completed, total } = goalProgressCounts.get(goalId) ?? {
    completed: 0,
    total: 0,
  };

  return { start: 0, current: completed, end: total };
};

// Resolved where the counts are built, with links, so no caller can resolve a different sub-goal set.
const getSubGoalProgressCompletion = (
  goalId: ID,
  goalProgressCounts?: Map<ID, GoalProgressCount>,
): GoalCompletion => {
  if (!goalProgressCounts) {
    return { start: 0, current: 0, end: Infinity };
  }

  const { progress, count } = goalProgressCounts.get(goalId)?.subGoals ?? {
    progress: 0,
    count: 0,
  };

  return { start: 0, current: progress, end: count };
};

export const getGoalCompletion = (
  goal: Goal,
  { goalProgressCounts, index }: GetGoalCompletionOptions,
): GoalCompletion => {
  // The index is keyed on goals, so a cache entry that reads task counts goes stale unseen.
  const isCacheable =
    !isTaskMeasuredGoal(goal) &&
    goal.metric?.id !== DefaultMetricOptions.SubGoalProgress;

  const cached = isCacheable
    ? index?.completionByGoalId.get(goal.id)
    : undefined;
  if (cached) {
    return cached;
  }

  const completion = computeGoalCompletion(goal, goalProgressCounts);
  if (isCacheable) {
    index?.completionByGoalId.set(goal.id, completion);
  }

  return completion;
};

const computeGoalCompletion = (
  goal: Goal,
  goalProgressCounts?: Map<ID, GoalProgressCount>,
): GoalCompletion => {
  // Projects ignore any metric carried over from when the item was a goal
  if (goal.type === GoalType.Project) {
    return getProgressCountCompletion(goal.id, goalProgressCounts);
  }

  if (!goal.metric?.id) {
    return {
      start: 0,
      current: 0,
      end: Infinity,
    };
  }

  if (goal.metric?.id === DefaultMetricOptions.CompletedTasks) {
    return getProgressCountCompletion(goal.id, goalProgressCounts);
  }

  if (goal.metric?.id === DefaultMetricOptions.SubGoalProgress) {
    return getSubGoalProgressCompletion(goal.id, goalProgressCounts);
  }

  const start = goal.metric?.startValue ?? 0;

  return {
    start,
    end: goal.metric?.targetValue ?? Infinity,
    current: getLatestMetricValue(goal.metric) ?? start,
  };
};
